/**
 * `root.querySelectorAll(selector)` only finds descendants of `root`, never `root` itself. Mutation
 * scanning needs both: `wk-icon` re-rendering its own children fires with `mutation.target` set to the
 * `wk-icon` itself (not one of `addedNodes`), so the selector has to be checked against the root too.
 */
function selectAll(root, selector) {
  const matches = [...root.querySelectorAll(selector)];
  if (root.matches?.(selector)) matches.unshift(root);
  return matches;
}

/**
 * Swish renders the raw `product.vendor` string with no knowledge of this store's "no vendor" convention —
 * blocks/nova-product-vendor.liquid hides the vendor line when it's blank OR literally the string "None"
 * (some products have that literal string set as vendor instead of being left blank). Mirror that rule here
 * so the wishlist page doesn't show "None" as a brand name.
 */
function hideNoneVendors(root) {
  selectAll(root, '.wk-vendor').forEach((el) => {
    if (el.textContent.trim() === 'None') el.style.display = 'none';
  });
}

/**
 * Clones the cart icon from `#wishlist-cta-icon-template` (see wishlist-page-style.liquid) in front of the
 * label on the per-card CTA (which Swish renders with no icon at all) and the buy-all button (whose own
 * `wk-icon[icon="buy"]` is hidden via CSS instead of removed, so this stays idempotent across re-renders).
 */
function insertCartIcons(root) {
  const template = document.getElementById('wishlist-cta-icon-template');
  if (!template) return;

  selectAll(root, '.wk-cta-button, wishlist-add-to-cart .wk-button').forEach((button) => {
    if (button.querySelector('.wk-cta-icon')) return;
    const label = button.querySelector('.wk-cta-label, .wk-text');
    const icon = template.content.firstElementChild.cloneNode(true);
    if (label) label.before(icon);
    else button.prepend(icon);
  });
}

/**
 * Both the floating "X" over the card image (`remove-button`) and the "Supprimer vos coups de coeur" clear
 * button render Swish's own `wk-icon[icon="remove"]` — swap it for the cart drawer's trash icon so both
 * remove actions share one visual language (assets/icon-delete.svg via #wishlist-remove-icon-template).
 *
 * `wk-icon` is itself a reactive Lit component that re-renders its own SVG back in after we've already
 * replaced it once (observed live: our clone and Swish's original ended up as siblings), so this can't be a
 * one-time swap gated by a "done" flag — it strips anything that isn't our clone on every pass instead,
 * relying on the page's MutationObserver to re-run this after Swish's re-render mutates the DOM again.
 */
function replaceRemoveIcons(root) {
  const template = document.getElementById('wishlist-remove-icon-template');
  if (!template) return;

  selectAll(root, 'wk-icon[icon="remove"]').forEach((wkIcon) => {
    [...wkIcon.children].forEach((child) => {
      if (!child.classList.contains('wk-remove-icon')) child.remove();
    });
    if (!wkIcon.querySelector('.wk-remove-icon')) {
      wkIcon.appendChild(template.content.firstElementChild.cloneNode(true));
    }
  });
}

/**
 * Every write below is guarded against writing a value that's already set. These run out of the
 * MutationObserver at the bottom of this file, and re-setting an attribute (or a `textContent`) to its
 * current value still queues a mutation record — an unguarded write would feed straight back into the
 * observer and spin forever. `classList.toggle(name, false)` on an element without the class is already a
 * no-op per spec, but `classList.add` of a class that's present is not, so both go through the same guard.
 */
function setClass(element, name, present) {
  if (element.classList.contains(name) !== present) element.classList.toggle(name, present);
}

function setAttribute(element, name, value) {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

function setText(element, value) {
  if (element.textContent !== value) element.textContent = value;
}

/**
 * Folds a size option's 4th and later values into an accordion.
 *
 * Swish lays every value of an option out as a sibling `.wk-swatch` in one wrapping row, which on a card
 * this narrow runs several lines deep for a long size run and pushes the add-to-cart button down. Only the
 * first three stay visible; the rest are marked `.wk-swatch--overflow` (hidden by CSS in
 * snippets/wishlist-page-style.liquid) behind a disclosure button cloned from
 * `#wishlist-swatch-toggle-template`.
 *
 * Color options are left alone: they're the groups Swish flags `.wk-has-color`, and they render as 28px
 * circles that fit six or more to a row, so they don't run the card long the way the text pills (Taille and
 * anything else without a color, styled after the PDP's variant pills) do.
 *
 * The button is inserted *in front of* the first hidden value rather than appended at the end of the
 * fieldset, so the DOM order matches what the CSS paints — the toggle above the values it reveals — and tab
 * order doesn't jump backwards once the accordion is open.
 *
 * Like the icon work above this is idempotent and re-runnable rather than one-time setup: the option group
 * is a Lit component that re-renders itself (picking a value re-paints the whole group), dropping anything
 * we injected.
 */
const VISIBLE_SWATCH_COUNT = 3;
const autoExpandedOptions = new WeakSet();
let swatchAccordionCount = 0;

function collapseExtraSwatches(root) {
  const template = document.getElementById('wishlist-swatch-toggle-template');
  if (!template) return;

  selectAll(root, 'wk-option-swatches').forEach((option) => {
    const fieldset = option.querySelector('.wk-fieldset');
    if (!fieldset) return;

    const swatches = [...fieldset.querySelectorAll('.wk-swatch')];
    if (swatches.some((swatch) => swatch.classList.contains('wk-has-color'))) return;

    const overflow = swatches.slice(VISIBLE_SWATCH_COUNT);
    swatches.forEach((swatch, index) => {
      setClass(swatch, 'wk-swatch--overflow', index >= VISIBLE_SWATCH_COUNT);
    });

    let toggle = fieldset.querySelector('.wk-swatch-toggle');

    /* An option that no longer overflows (Swish re-rendered it with fewer values) has to lose its toggle,
       or the card keeps a disclosure that opens onto nothing. */
    if (overflow.length === 0) {
      toggle?.remove();
      setClass(option, 'wk-swatches-expanded', false);
      return;
    }

    if (!toggle) toggle = template.content.firstElementChild.cloneNode(true);
    if (toggle.nextElementSibling !== overflow[0]) overflow[0].before(toggle);

    /* `aria-controls` names the hidden values themselves rather than the fieldset: the fieldset also holds
       the three visible values *and* the toggle, so pointing at it would have the button claim to control
       its own ancestor. It takes an ID list, so the overflow gets ids of its own, keyed off the fieldset so
       two option groups on the same card can't collide. */
    if (!fieldset.id) fieldset.id = `wishlist-swatches-${(swatchAccordionCount += 1)}`;
    overflow.forEach((swatch, index) => {
      if (!swatch.id) swatch.id = `${fieldset.id}-value-${VISIBLE_SWATCH_COUNT + index}`;
    });
    setAttribute(toggle, 'aria-controls', overflow.map((swatch) => swatch.id).join(' '));

    /* A value the customer has already chosen must never be the hidden one, so the accordion opens itself
       the first time a selection lands past the third swatch. Only the first time: after that a manual
       collapse has to stick, otherwise the button would look broken while that value stays selected. */
    if (!autoExpandedOptions.has(option)) {
      const selectsHiddenValue = overflow.some(
        (swatch) => swatch.classList.contains('wk-selected') || swatch.querySelector('.wk-radio')?.checked
      );
      if (selectsHiddenValue) {
        autoExpandedOptions.add(option);
        setClass(option, 'wk-swatches-expanded', true);
      }
    }

    const expanded = option.classList.contains('wk-swatches-expanded');
    setAttribute(toggle, 'aria-expanded', String(expanded));
    setText(
      toggle.querySelector('.wk-swatch-toggle__label'),
      expanded ? toggle.dataset.labelLess : `${toggle.dataset.labelMore} (+${overflow.length})`
    );
  });
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.wk-swatch-toggle');
  const option = toggle?.closest('wk-option-swatches');
  if (!option) return;

  option.classList.toggle('wk-swatches-expanded');
  /* Re-runs rather than updating the button inline so the label, `aria-expanded` and the overflow classes
     are only ever derived in one place. */
  collapseExtraSwatches(option);
});

function processAddedContent(root) {
  hideNoneVendors(root);
  insertCartIcons(root);
  replaceRemoveIcons(root);
  collapseExtraSwatches(root);
}

/**
 * Records how tall this visitor's rendered wishlist actually is, so the next load can reserve that exact
 * space before paint instead of collapsing to 0px and jumping (see the `#MainContent:has(#wishlist-page)`
 * rule and the inline restore script in snippets/wishlist-page-style.liquid — this is the write half of
 * that pair).
 *
 * Stored with the viewport width it was measured at: card heights are viewport-dependent, and the restore
 * side discards a height recorded at a materially different width rather than reserve the wrong space.
 *
 * Debounced because it's driven by the same MutationObserver as the icon work below, which Swish fires
 * repeatedly during a render — the height is only meaningful once the DOM settles.
 */
const HEIGHT_STORAGE_KEY = 'wishlist-page-height';
let heightRecordTimer;
let recordedHeight = 0;

function recordRenderedHeight() {
  clearTimeout(heightRecordTimer);
  heightRecordTimer = setTimeout(() => {
    const height = Math.round(document.querySelector('wishlist-page')?.getBoundingClientRect().height ?? 0);
    if (!height || height === recordedHeight) return;
    recordedHeight = height;

    try {
      localStorage.setItem(HEIGHT_STORAGE_KEY, JSON.stringify({ width: window.innerWidth, height }));
    } catch (error) {
      /* Private mode, disabled storage, or quota — the next load just falls back to the CSS reservation. */
    }
  }, 500);
}

processAddedContent(document);
recordRenderedHeight();
/**
 * Scans only `mutation.target` and `mutation.addedNodes` per mutation instead of rescanning `document` on
 * every mutation (Swish mutates the DOM frequently). Both are needed: `wk-icon` re-rendering its own
 * children shows up as `mutation.target` (the `wk-icon` itself), never as one of `addedNodes` — only its
 * new children are added nodes. `selectAll` covers the "root itself matches" half of that; collecting
 * `target` alongside `addedNodes` here covers the other half.
 */
new MutationObserver((mutations) => {
  const roots = new Set();
  mutations.forEach((mutation) => {
    if (mutation.target.nodeType === Node.ELEMENT_NODE) roots.add(mutation.target);
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) roots.add(node);
    });
  });
  roots.forEach((root) => processAddedContent(root));
  recordRenderedHeight();
}).observe(document.body, {
  childList: true,
  subtree: true,
});

/**
 * Reuses the theme's PDP "fly to cart" animation (see the `fly-to-cart` custom element registered by
 * @theme/product-form, loaded globally via snippets/scripts.liquid) for Swish's wishlist-page add-to-cart
 * button (`.wk-cta-button`). Swish's button lives inside its own Lit component tree, not theme markup, so
 * there's no ref to hook into `AddToCartComponent` directly — a delegated click listener is the only
 * available seam, same approach as assets/wishlist-header-link.js's heart-burst animation.
 *
 * Fired optimistically on click (matching AddToCartComponent#handleClick, which doesn't wait for the
 * cart/add response either) rather than after Swish's fetch resolves, since Swish exposes no add-success
 * event to hook into.
 */
document.addEventListener('click', (event) => {
  const button = event.target.closest('.wk-cta-button');
  if (!button || button.disabled) return;

  const cartIcon = document.querySelector('.header-actions__cart-icon');
  if (!cartIcon || !customElements.get('fly-to-cart')) return;

  const image = button.closest('.wk-product-card')?.querySelector('.wk-image');
  const imageSrc = image?.currentSrc || image?.src;

  const flyToCartElement = document.createElement('fly-to-cart');
  if (imageSrc) flyToCartElement.style.setProperty('background-image', `url(${imageSrc})`);
  flyToCartElement.source = button;
  flyToCartElement.destination = cartIcon;

  document.body.appendChild(flyToCartElement);
});
